import { useState, useEffect } from 'react';
import api from '../../services/api';

function UsageLimits({ compact = false }) {
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadUsage();
  }, []);

  const loadUsage = async () => {
    try {
      const response = await api.get('/users/me/usage/detailed');
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to load usage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="animate-pulse bg-gray-700 h-20 rounded-lg"></div>;
  }

  if (!usage) return null;

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-primary-500';
  };

  const characterPercent = usage.limits?.characters_per_month 
    ? Math.min((usage.characters_used / usage.limits.characters_per_month) * 100, 100)
    : 0;

  const voiceClonesPercent = usage.limits?.voice_clones
    ? Math.min((usage.voice_clones_used / usage.limits.voice_clones) * 100, 100)
    : 0;

  if (compact) {
    return (
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Characters</span>
            <span className="text-gray-400">
              {usage.characters_used?.toLocaleString()} / {usage.limits?.characters_per_month?.toLocaleString() || '∞'}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getProgressColor(characterPercent)}`}
              style={{ width: `${characterPercent}%` }}
            />
          </div>
        </div>

        {usage.limits?.voice_clones && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Voice Clones</span>
              <span className="text-gray-400">
                {usage.voice_clones_used} / {usage.limits.voice_clones}
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${getProgressColor(voiceClonesPercent)}`}
                style={{ width: `${voiceClonesPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="text-lg font-semibold mb-4">Usage This Month</h3>
      
      <div className="space-y-6">
        {/* Characters */}
        <div>
          <div className="flex justify-between mb-2">
            <span className="font-medium">Characters Used</span>
            <span className="text-gray-400">
              {usage.characters_used?.toLocaleString()} / {usage.limits?.characters_per_month?.toLocaleString() || 'Unlimited'}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${getProgressColor(characterPercent)}`}
              style={{ width: `${characterPercent}%` }}
            />
          </div>
          {characterPercent >= 90 && (
            <p className="text-sm text-yellow-400 mt-1">
              ⚠️ You're running low on characters. Consider upgrading your plan.
            </p>
          )}
        </div>

        {/* Voice Clones */}
        {usage.limits?.voice_clones && (
          <div>
            <div className="flex justify-between mb-2">
              <span className="font-medium">Voice Clones</span>
              <span className="text-gray-400">
                {usage.voice_clones_used} / {usage.limits.voice_clones}
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${getProgressColor(voiceClonesPercent)}`}
                style={{ width: `${voiceClonesPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* TTS Conversions Today */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-700">
          <div>
            <p className="text-gray-400 text-sm">Conversions Today</p>
            <p className="text-2xl font-bold">{usage.conversions_today || 0}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Total Conversions</p>
            <p className="text-2xl font-bold">{usage.total_conversions || 0}</p>
          </div>
        </div>

        {/* Plan Info */}
        <div className="pt-4 border-t border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Current Plan</p>
              <p className="font-semibold capitalize">{usage.plan || 'Free'}</p>
              <p className="text-gray-400 text-sm mt-1">
                Workspace Seats: {usage.limits?.workspace_seats || 1}
              </p>
            </div>
            {usage.plan === 'free' && (
              <a href="/billing" className="btn btn-primary text-sm">
                Upgrade Plan
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsageLimits;
